import { jsx as _jsx } from "react/jsx-runtime";
/**
 * 组件文案。
 *
 * 全部可覆盖：框架给的默认文案只能做到"不出错"，做不到"贴你的产品语气"。
 * 但默认文案本身是**认真写过的**——特别是安装引导和权限说明那几句，
 * 它们直接决定用户装不装得成、授不授权。
 */
import { createContext, useContext, useMemo } from 'react';
export const zh = {
    install: {
        bannerText: '添加到主屏幕，用起来像个 App',
        bannerAction: '怎么加',
        added: '已添加，不再提示',
        dismiss: '关闭',
        guideTitle: '添加到主屏幕',
        // 各家浏览器的菜单路径完全不同，照着别家的说明点是找不到的
        iosSafari: ['点底部的 分享 按钮', '在列表里往下翻', '点「添加到主屏幕」', '右上角「添加」'],
        iosOther: ['点地址栏旁的 分享 按钮', '选「添加到主屏幕」', '确认添加'],
        android: ['点右上角的 ⋮ 菜单', '选「安装应用」或「添加到主屏幕」', '确认安装'],
        desktop: '在地址栏右侧点安装图标，或从浏览器菜单里选「安装」。',
        iosFallback: '不同 iOS 版本菜单位置略有出入。找不到就在分享面板里往下翻，一定有「添加到主屏幕」。',
        iconChangeNotice: '换了图标后，**已经放在桌面上的那个图标不会变**——系统在你添加到主屏那一刻就把它缓存了。要看到新图标，请删掉桌面图标重新添加一次。',
    },
    notification: {
        primerTitle: '开启通知',
        primerBody: '开启后，有新消息时你会第一时间知道，不用一直开着这个页面。',
        primerAllow: '开启通知',
        primerLater: '以后再说',
        denied: '通知权限被拒绝了。浏览器不会再次询问，需要到系统设置里手动打开。',
        needsInstall: '在 iPhone / iPad 上，需要先把本应用添加到主屏幕，并**从主屏图标打开**，才能收到通知。',
        unsupported: '当前浏览器不支持通知。',
        gestureError: '通知授权需要你亲自点一下才能发起，请重新点击按钮。',
        otherError: '没能开启通知，请稍后再试。',
    },
    update: {
        title: '有新版本',
        action: '立即更新',
        later: '稍后',
        // 这句必须说：不说的话用户（和你自己）会以为更新失败了
        restartHint: '如果更新后还是旧的，把应用完全退出再打开一次。',
    },
    offline: {
        banner: '当前离线，部分内容可能不是最新的',
        restored: '网络已恢复',
    },
    common: { close: '关闭', retry: '重试', cancel: '取消', confirm: '确定' },
};
export const en = {
    install: {
        bannerText: 'Add to Home Screen for an app-like experience',
        bannerAction: 'How',
        added: 'Already added — stop asking',
        dismiss: 'Dismiss',
        guideTitle: 'Add to Home Screen',
        iosSafari: ['Tap the Share button at the bottom', 'Scroll down the list', 'Tap "Add to Home Screen"', 'Tap "Add"'],
        iosOther: ['Tap the Share button next to the address bar', 'Choose "Add to Home Screen"', 'Confirm'],
        android: ['Tap the ⋮ menu', 'Choose "Install app" or "Add to Home screen"', 'Confirm'],
        desktop: 'Click the install icon in the address bar, or pick "Install" from the browser menu.',
        iosFallback: 'Menu positions vary between iOS versions. Scroll the share sheet — "Add to Home Screen" is always there.',
        iconChangeNotice: 'After changing the icon, the one already on your Home Screen will NOT update — the system cached it the moment you added it. Delete the Home Screen icon and add it again.',
    },
    notification: {
        primerTitle: 'Turn on notifications',
        primerBody: "You'll hear about new messages right away, without keeping this page open.",
        primerAllow: 'Turn on',
        primerLater: 'Not now',
        denied: 'Notifications are blocked. The browser will not ask again — enable them in system settings.',
        needsInstall: 'On iPhone / iPad you must add this app to the Home Screen and open it from that icon to receive notifications.',
        unsupported: 'This browser does not support notifications.',
        gestureError: 'Permission must be requested directly from your tap. Please tap the button again.',
        otherError: 'Could not enable notifications. Please try again later.',
    },
    update: {
        title: 'A new version is available',
        action: 'Update now',
        later: 'Later',
        restartHint: 'If it still looks old, fully quit the app and reopen it.',
    },
    offline: { banner: "You're offline — some content may be out of date", restored: 'Back online' },
    common: { close: 'Close', retry: 'Retry', cancel: 'Cancel', confirm: 'OK' },
};
const StringsContext = createContext(zh);
export function useStrings() {
    return useContext(StringsContext);
}
function merge(base, override) {
    const out = { ...base };
    for (const key of Object.keys(override)) {
        const value = override[key];
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            out[key] = { ...base[key], ...value };
        }
    }
    return out;
}
export function StringsProvider({ strings = 'zh', children }) {
    const value = useMemo(() => {
        if (strings === 'zh')
            return zh;
        if (strings === 'en')
            return en;
        return merge(zh, strings);
    }, [strings]);
    return _jsx(StringsContext.Provider, { value: value, children: children });
}
//# sourceMappingURL=i18n.js.map